/**
 * Stepping into a station (2026-10-13).
 *
 * A station's panel does not pop up: it grows out of the station you pressed
 * (FLIP - the card is measured in its final place and animated from the
 * station's rect), and the station's own layered art becomes a parallax banner
 * across the top of the panel, so the Quests panel is the quests room. Closing
 * runs it backwards on a hollow ghost of the card (the real card is hidden at
 * once, as `setPanel` always did).
 *
 * This only wraps `setPanel`; it never changes what is shown. Under reduced
 * motion the banner still appears, nothing moves.
 */

import { HALL_ID, buildLayerStack, stationFallbackUrl, stationLayerId } from './hub-depth';

interface RectLike { left: number; top: number; width: number; height: number }

export interface Flip { dx: number; dy: number; scale: number }

/**
 * Centre-to-centre translate and one uniform scale (a non-uniform one smears
 * the card's text mid-flight). Scale is the width ratio, floored so a tiny
 * origin does not start the card as a speck.
 */
export function flipFrom(origin: RectLike, target: RectLike, viewportHeight = Infinity): Flip {
  const ocx = origin.left + origin.width / 2;
  let ocy = origin.top + origin.height / 2;
  // A station scrolled off-screen: start from the screen edge, not from afar.
  ocy = Math.min(Math.max(ocy, 0), viewportHeight);
  const tcx = target.left + target.width / 2;
  const tcy = target.top + target.height / 2;
  const w = target.width || 1;
  return {
    dx: ocx - tcx,
    dy: ocy - tcy,
    scale: Math.min(1, Math.max(0.16, origin.width / w)),
  };
}

/** Which element a panel grows out of: its station, or the control that opens it. */
export function originSelector(panel: string): string | null {
  switch (panel) {
    case 'characters': return '[data-tile="characters"]';
    case 'play': return '#hub-play-btn';
    case 'inventory': return '#hub-inventory-btn';
    default: return stationLayerId(panel) ? `.station[data-tile="${panel}"]` : null;
  }
}

/** The layered art a panel shows: its station's, else the hall's. */
export function bannerSource(panel: string): { id: string; fallback: string | null } {
  const id = stationLayerId(panel);
  return id ? { id, fallback: stationFallbackUrl(panel) } : { id: HALL_ID, fallback: null };
}

export interface PanelPortal {
  /** Call after the panel's sections are un-hidden and titled, with whether the overlay was already open. */
  enter(panel: string, wasOpen: boolean): void;
  /** Call BEFORE the overlay is hidden. */
  exit(panel: string | null): void;
}

const OPEN_MS = 440;
const CLOSE_MS = 280;
const EASE = 'cubic-bezier(0.2, 0.7, 0.3, 1)';

export function createPanelPortal(root: HTMLElement, card: HTMLElement): PanelPortal {
  const banner = document.createElement('div');
  banner.id = 'hub-panel-banner';
  banner.className = 'panel-banner';
  banner.setAttribute('aria-hidden', 'true');
  card.insertBefore(banner, card.firstChild);

  const stacks = new Map<string, HTMLElement>();
  let currentId = '';

  const reduced = (): boolean =>
    typeof window.matchMedia !== 'function' || window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const refreshBanner = (): void => {
    const stack = stacks.get(currentId);
    const show = !!stack && stack.dataset.state !== 'none';
    card.classList.toggle('has-banner', show);
    banner.hidden = !show;
  };

  function setBanner(panel: string): void {
    const src = bannerSource(panel);
    currentId = src.id;
    let stack = stacks.get(src.id);
    if (!stack) {
      stack = buildLayerStack(src.id, { fallbackUrl: src.fallback });
      stack.addEventListener('depthstate', () => { if (stacks.get(currentId) === stack) refreshBanner(); });
      stacks.set(src.id, stack);
    }
    if (stack.parentElement !== banner) {
      banner.replaceChildren(stack);
    }
    refreshBanner();
  }

  function originRect(panel: string): RectLike | null {
    const sel = originSelector(panel);
    const el = sel ? root.querySelector<HTMLElement>(sel) : null;
    if (!el) return null;
    const r = el.getBoundingClientRect();
    return r.width > 0 && r.height > 0 ? r : null;
  }

  return {
    enter(panel, wasOpen): void {
      setBanner(panel);
      if (wasOpen || reduced() || typeof card.animate !== 'function') return;
      const origin = originRect(panel);
      if (!origin) return;
      const f = flipFrom(origin, card.getBoundingClientRect(), window.innerHeight);
      card.animate(
        [
          { transform: `translate3d(${f.dx}px, ${f.dy}px, 0) scale(${f.scale})`, opacity: 0.2, filter: 'brightness(1.6)' },
          { transform: 'none', opacity: 1, filter: 'none' },
        ],
        { duration: OPEN_MS, easing: EASE },
      );
      // Banner art rushes toward the camera as the card lands.
      if (typeof banner.animate === 'function') {
        banner.animate(
          [{ transform: 'scale(1.35)', opacity: 0.4 }, { transform: 'none', opacity: 1 }],
          { duration: OPEN_MS + 160, easing: EASE },
        );
      }
    },

    exit(panel): void {
      if (!panel || reduced() || typeof document.body.animate !== 'function') return;
      const r = card.getBoundingClientRect();
      if (r.width === 0) return;
      const origin = originRect(panel);
      const f = origin ? flipFrom(origin, r, window.innerHeight) : { dx: 0, dy: 24, scale: 0.92 };
      const ghost = document.createElement('div');
      ghost.className = 'portal-ghost';
      ghost.setAttribute('aria-hidden', 'true');
      ghost.style.cssText = `left:${r.left}px;top:${r.top}px;width:${r.width}px;height:${r.height}px;`;
      document.body.appendChild(ghost);
      const anim = ghost.animate(
        [
          { transform: 'none', opacity: 0.9 },
          { transform: `translate3d(${f.dx}px, ${f.dy}px, 0) scale(${f.scale})`, opacity: 0 },
        ],
        { duration: CLOSE_MS, easing: 'cubic-bezier(0.5, 0, 0.8, 0.4)', fill: 'forwards' },
      );
      const done = (): void => { ghost.remove(); };
      anim.onfinish = done;
      anim.oncancel = done;
    },
  };
}
